import type { OnDestroy, OnInit } from '@angular/core';
import type { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import type { ThemeColor, ThemeColorName } from '@sites/site-theme/src/types';
// The WIDGET button shape. This editor is placed by ~14 widget settings panels and by the theme's
// own panel; the widgets are the ones whose data it reads and writes, and they store their lengths
// as `8` or `'8px'`. The theme moved to `{ value, unit }` in v0 and has `sp-custom-button-v2`.
import type {
    ThemeButtonDataLegacy as ThemeButtonData,
    ThemeButtonHoverLegacy as ThemeButtonHover,
} from '@sites/entity-core';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { AdvancedBorderSettings } from '../sp-advanced-border-settings/type/advanced-border-settings.type';

import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BUTTON_HOVER_TYPE_OPTIONS } from '@sites/site-theme/src/config/vocabularies.const';

import { CSS_RESTRICTIONS } from '@common/constants';
import { InputBoolean } from '@common/helpers/convert';
import { EButtonHoverType } from '@common/enums/button.enum';
import { ELineStyle } from '@common/enums/line-style.enum';

import type { ButtonMode, CustomButtonValue } from './type/custom-button.type';
import type { AdvancedRadiusSettings } from '../sp-advanced-radius-settings/type/sp-advanced-radius-settings.type';
import { SpColorPickerComponent } from '../sp-color-picker/src/sp-color-picker.component';
import { SpInputNumComponent } from '../sp-form-controls/sp-input-num/sp-input-num.component';
import { SpAdvancedRadiusSettingsComponent } from '../sp-advanced-radius-settings/sp-advanced-radius-settings.component';
import { SpAdvancedBorderSettingsComponent } from '../sp-advanced-border-settings/sp-advanced-border-settings.component';

@Component({
    selector: 'sp-custom-button',
    templateUrl: './sp-custom-button.component.html',
    styleUrls: ['./sp-custom-button.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslateModule,
        TooltipModule,
        SpColorPickerComponent,
        SpInputNumComponent,
        SpAdvancedRadiusSettingsComponent,
        SpAdvancedBorderSettingsComponent,
    ],
})
export class SpCustomButtonComponent implements OnInit, OnDestroy {
    private readonly fb = inject(NonNullableFormBuilder);

    @Input() public parentBase?: Partial<ThemeButtonData> = {};
    @Input() public parentHover?: ThemeButtonHover;
    @Input() public colorsPreset: ThemeColor[] = [];
    @Input() public buttonMode: ButtonMode = null;
    @Input() @InputBoolean() public isThemeSettings: boolean = false;
    @Input() @InputBoolean() public showFontSettings: boolean = false;
    @Input() @InputBoolean() public showHoverSettings: boolean = false;
    @Input() public isAdvancedRadius?: boolean;

    @Input() @InputBoolean() public isMiniSiteButton: boolean = false;
    @Input() @InputBoolean() public isOnlyHover: boolean = false;
    @Input() public isWebsiteThemeButton?: boolean;
    @Input() public isIndependentCorners?: boolean;
    @Input() public radiusSettings?: AdvancedRadiusSettings;
    @Input() public advancedBorderSettings?: AdvancedBorderSettings;

    @Output() public valueChangeEvent: EventEmitter<CustomButtonValue> = new EventEmitter();

    private form: UntypedFormGroup;
    private readonly unsubscribe$ = new Subject<void>();

    public EButtonHoverType = EButtonHoverType;
    public buttonHoverOptions = BUTTON_HOVER_TYPE_OPTIONS;

    constructor() {
        this.initForm();
    }

    public ngOnInit(): void {
        this.form.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((changes) => this.emitChanges(changes));

        this.setFormData();
    }

    private initForm(): void {
        this.form = this.fb.group({
            base: this.fb.group({
                color: null,
                background: null,
                borderColor: null,
                borderWidth: null,
                borderRadius: null,
                borderStyle: null,
            }),
            hover: this.fb.group({
                hoverType: null,
                opacity: null,
                shadow: null,
                color: null,
                background: null,
                borderColor: null,
            }),
            isIndependentCorners: null,
            radiusSettings: this.fb.group({
                borderTopLeftRadius: null,
                borderTopRightRadius: null,
                borderBottomLeftRadius: null,
                borderBottomRightRadius: null,
            }),
            advancedBorderSettings: null,
        });
    }

    private setFormData(): void {
        const base: ThemeButtonData = {
            color: this.parentBase.color,
            background: this.parentBase.background,
            borderColor: this.parentBase.borderColor,
            borderWidth: parseInt(this.parentBase.borderWidth as string, 10),
            borderRadius: parseInt(this.parentBase.borderRadius as string, 10),
            borderStyle: this.parentBase.borderStyle,
        };

        if (!this.showHoverSettings) {
            this.form.patchValue({ base }, { emitEvent: false });
            return;
        }

        const isIndependentCorners: boolean = this.isIndependentCorners;

        const radiusSettings: AdvancedRadiusSettings = {
            borderTopLeftRadius: this.radiusSettings?.borderTopLeftRadius
                ? this.radiusSettings?.borderTopLeftRadius
                : +this.parentBase?.borderRadius,
            borderTopRightRadius: this.radiusSettings?.borderTopRightRadius
                ? this.radiusSettings?.borderTopRightRadius
                : +this.parentBase?.borderRadius,
            borderBottomLeftRadius: this.radiusSettings?.borderBottomLeftRadius
                ? this.radiusSettings?.borderBottomLeftRadius
                : +this.parentBase?.borderRadius,
            borderBottomRightRadius: this.radiusSettings?.borderBottomRightRadius
                ? this.radiusSettings?.borderBottomRightRadius
                : +this.parentBase?.borderRadius,
        };

        const hover: ThemeButtonHover = {
            hoverType: this.parentHover.hoverType,
            opacity: this.parentHover.opacity,
            shadow: this.parentHover.shadow,
            color: this.parentHover.color,
            background: this.parentHover.background,
            borderColor: this.parentHover.borderColor,
        };

        if (this.isAdvancedRadius) {
            const advancedBorderSettings = this.advancedBorderSettings ?? null;

            this.form.patchValue(
                { base, hover, isIndependentCorners, radiusSettings, advancedBorderSettings },
                { emitEvent: false }
            );
            return;
        }

        this.form.patchValue({ base, hover, isIndependentCorners }, { emitEvent: false });
    }

    private emitChanges(changes: CustomButtonValue): void {
        this.valueChangeEvent.emit(changes);
    }

    private setColorPickerPayload(property: string, value: string, presetName: ThemeColorName): void {
        let formPropertyValue: string | string[];
        const payloadValue: string = presetName || value;

        if (Array.isArray(this.color.value)) {
            formPropertyValue = [...this.base.value[property]];
            formPropertyValue[this.buttonMode] = payloadValue;
            if (formPropertyValue.length > 1 && formPropertyValue.every((item: string) => item === payloadValue)) {
                formPropertyValue = [payloadValue];
            }
        } else {
            formPropertyValue = payloadValue;
        }

        this.base.setValue({ ...this.base.value, [property]: formPropertyValue });
    }

    private setHoverColorPickerPayload(property: string, value: string, presetName: ThemeColorName): void {
        let formPropertyValue: string | string[];
        const payloadValue: string = presetName || value;

        if (Array.isArray(this.color.value)) {
            formPropertyValue = [...this.hover.value[property]];
            formPropertyValue[this.buttonMode] = payloadValue;
            if (formPropertyValue.length > 1 && formPropertyValue.every((item: string) => item === payloadValue)) {
                formPropertyValue = [payloadValue];
            }
        } else if (this.isMiniSiteButton) {
            formPropertyValue = [payloadValue];
        } else {
            formPropertyValue = payloadValue;
        }
        this.hover.setValue({ ...this.hover.value, [property]: formPropertyValue });
    }

    public get isIndependentCornersValue(): AbstractControl {
        return this.form.get('isIndependentCorners');
    }

    public get radiusSettingsData(): AbstractControl {
        return this.form.get('radiusSettings');
    }

    public get borderTopLeftRadius(): AbstractControl {
        return this.radiusSettingsData.get('borderTopLeftRadius');
    }

    public get borderTopRightRadius(): AbstractControl {
        return this.radiusSettingsData.get('borderTopRightRadius');
    }

    public get borderBottomLeftRadius(): AbstractControl {
        return this.radiusSettingsData.get('borderBottomLeftRadius');
    }

    public get borderBottomRightRadius(): AbstractControl {
        return this.radiusSettingsData.get('borderBottomRightRadius');
    }

    public setAdvancedRadiusSettings(updatedSettings: AdvancedRadiusSettings) {
        if (updatedSettings.borderTopLeftRadius !== this.radiusSettings.borderTopLeftRadius) {
            this.borderTopLeftRadius.setValue(updatedSettings.borderTopLeftRadius);
        }

        if (updatedSettings.borderTopRightRadius !== this.radiusSettings.borderTopRightRadius) {
            this.borderTopRightRadius.setValue(updatedSettings.borderTopRightRadius);
        }

        if (updatedSettings.borderBottomRightRadius !== this.radiusSettings.borderBottomRightRadius) {
            this.borderBottomRightRadius.setValue(updatedSettings.borderBottomRightRadius);
        }

        if (updatedSettings.borderBottomLeftRadius !== this.radiusSettings.borderBottomLeftRadius) {
            this.borderBottomLeftRadius.setValue(updatedSettings.borderBottomLeftRadius);
        }
    }

    public get base(): AbstractControl {
        return this.form.get('base');
    }

    // COLOR
    public get color(): AbstractControl {
        return this.base.get('color');
    }

    public get colorValue(): string {
        if (this.buttonMode !== null) {
            return this.color.value[this.buttonMode] || this.color.value[0];
        }

        return this.color.value;
    }

    public setColor({ value, presetName }: ColorPickerEventPayload): void {
        this.setColorPickerPayload('color', value, presetName);

        if (!this.showHoverSettings) {
            return;
        }

        if (this.hoverType.value !== EButtonHoverType.color) {
            this.setHoverColor({ value, presetName });
        }
    }

    // BACKGROUND
    public get background(): AbstractControl {
        return this.base.get('background');
    }

    public get backgroundValue(): string {
        if (this.buttonMode !== null) {
            return this.background.value[this.buttonMode] || this.background.value[0];
        }

        return this.background.value;
    }

    public setBackground({ value, presetName }: ColorPickerEventPayload): void {
        this.setColorPickerPayload('background', value, presetName);

        if (!this.showHoverSettings) {
            return;
        }

        if (this.hoverType.value !== EButtonHoverType.color) {
            this.setHoverBackground({ value, presetName });
        }
    }

    // BORDER_WIDTH
    public get borderWidth(): AbstractControl {
        return this.base.get('borderWidth');
    }

    public setBorderWidth(value: number): void {
        this.borderWidth.setValue(value);
    }

    // BORDER_COLOR
    public get borderColor(): AbstractControl {
        return this.base.get('borderColor');
    }

    public get borderColorValue(): string {
        if (this.buttonMode !== null) {
            return this.borderColor.value[this.buttonMode] || this.borderColor.value[0];
        }

        return this.borderColor.value;
    }

    public setBorderColor({ value, presetName }: ColorPickerEventPayload): void {
        this.setColorPickerPayload('borderColor', value, presetName);

        if (!this.showHoverSettings) {
            return;
        }

        if (this.hoverType.value !== EButtonHoverType.color) {
            this.setHoverBorderColor({ value, presetName });
        }
    }

    // BORDER_RADIUS
    public get borderRadius(): AbstractControl {
        return this.base.get('borderRadius');
    }

    public setBorderRadius(value: number): void {
        this.borderRadius.setValue(value);

        this.borderTopLeftRadius.setValue(value);
        this.borderTopRightRadius.setValue(value);
        this.borderBottomRightRadius.setValue(value);
        this.borderBottomLeftRadius.setValue(value);
    }

    public get BORDER_WIDTH_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_WIDTH_MIN;
    }

    public get BORDER_WIDTH_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_WIDTH_MAX;
    }

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }

    public get hover(): AbstractControl {
        return this.form.get('hover');
    }

    public get hoverType(): AbstractControl {
        return this.hover.get('hoverType');
    }

    public changeHoverOptions(event: Event): void {
        const selectedOption = (event.target as HTMLSelectElement).value;

        if (selectedOption === EButtonHoverType.opacity) {
            this.hover.patchValue({
                hoverType: EButtonHoverType.opacity,
                opacity: 80,
                shadow: 'none',
                color: this.color.value,
                background: this.background.value,
                borderColor: this.borderColor.value,
            });
            return;
        }

        if (selectedOption === EButtonHoverType.shadow) {
            this.hover.patchValue({
                hoverType: EButtonHoverType.shadow,
                opacity: 100,
                shadow: '0 0 10px 6px rgb(0 0 0 / 10%)',
                color: this.color.value,
                background: this.background.value,
                borderColor: this.borderColor.value,
            });
            return;
        }

        if (selectedOption === EButtonHoverType.color) {
            this.hover.patchValue({
                hoverType: EButtonHoverType.color,
                opacity: 100,
                shadow: 'none',
                color: ['rgb(255,255,255)'],
                background: ['rgb(22,117,242)'],
                borderColor: ['rgba(0,0,0,0)'],
            });
        }
    }

    public get opacity(): AbstractControl {
        return this.hover.get('opacity');
    }

    public get OPACITY_MIN(): number {
        return CSS_RESTRICTIONS.OPACITY_MIN;
    }

    public get OPACITY_MAX(): number {
        return CSS_RESTRICTIONS.OPACITY_MAX;
    }

    public setOpacity(value: number): void {
        this.opacity.setValue(value);
    }

    // HOVER COLOR
    public get hoverColor(): AbstractControl {
        return this.hover.get('color');
    }

    public get hoverColorValue(): string {
        if (this.buttonMode !== null) {
            return this.hoverColor.value[this.buttonMode] || this.hoverColor.value[0];
        }

        return this.hoverColor.value.length ? this.hoverColor.value[0] : this.hoverColor.value;
    }

    public setHoverColor({ value, presetName }: ColorPickerEventPayload): void {
        this.setHoverColorPickerPayload('color', value, presetName);
    }

    // HOVER BACKGROUND
    public get hoverBackground(): AbstractControl {
        return this.hover.get('background');
    }

    public get hoverBackgroundValue(): string {
        if (this.buttonMode !== null) {
            return this.hoverBackground.value[this.buttonMode] || this.hoverBackground.value[0];
        }

        return this.hoverBackground.value.length ? this.hoverBackground.value[0] : this.hoverBackground.value;
    }

    public setHoverBackground({ value, presetName }: ColorPickerEventPayload): void {
        this.setHoverColorPickerPayload('background', value, presetName);
    }

    // HOVER BORDER_COLOR
    public get hoverBorderColor(): AbstractControl {
        return this.hover.get('borderColor');
    }

    public get hoverBorderColorValue(): string {
        if (this.buttonMode !== null) {
            return this.hoverBorderColor.value[this.buttonMode] || this.hoverBorderColor.value[0];
        }

        return this.hoverBorderColor.value.length ? this.hoverBorderColor.value[0] : this.hoverBorderColor.value;
    }

    public setHoverBorderColor({ value, presetName }: ColorPickerEventPayload): void {
        this.setHoverColorPickerPayload('borderColor', value, presetName);
    }

    public toggleIndenpendentCorners() {
        this.setBorderRadius(this.borderTopLeftRadius.value);

        this.isIndependentCornersValue.setValue(!this.isIndependentCornersValue.value);
    }

    public get advancedBorderSettingsControl(): AbstractControl {
        return this.form.get('advancedBorderSettings');
    }

    public get advancedBorderSettingsValue(): AdvancedBorderSettings {
        if (this.advancedBorderSettingsControl.value) {
            return this.advancedBorderSettingsControl.value;
        }

        const width = +(this.borderWidth.value ?? 2);
        const style = (this.base.get('borderStyle').value as ELineStyle) ?? ELineStyle.SOLID;
        const color = this.borderColor.value?.toString() ?? '#000000';

        return {
            isActive: true,
            isIndependentBorders: false,
            borderTopWidth: width,
            borderTopColor: color,
            borderTopStyle: style,
            borderRightWidth: width,
            borderRightColor: color,
            borderRightStyle: style,
            borderBottomWidth: width,
            borderBottomColor: color,
            borderBottomStyle: style,
            borderLeftWidth: width,
            borderLeftColor: color,
            borderLeftStyle: style,
        };
    }

    public setAdvancedBorderSettings(updatedSettings: AdvancedBorderSettings): void {
        this.advancedBorderSettingsControl.setValue({ ...updatedSettings, isActive: true });
    }

    public ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
